import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';

import theme from '../../../../assets/stylesheets/_settings.global.scss';
import Loading from '../../../shared/loading';
import {
  Button,
  ButtonGroup,
  Container,
  Desc,
  Separator,
  Title,
} from './common/styles';
import { subscribeToTrending, unsubscribeFromTrending } from '../../../services/profiles/notification-settings-service';

const SButtonGroup = styled(ButtonGroup)`
  span.loading-panel-inline {
    padding-top: 10px;
    margin-left: 20px;
  }
`;

const Hint = styled.div`
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightBold};
  font-size: 12px;
  line-height: 13px;
  color: #20BA9C;
  margin-top: 10px;
  margin-bottom: -20px;
`;

export default function TrendingControl(props) {
  const { subscribedToTrending, tolSubscriptionOptions, weeklyTOLEnabled } = props;
  const [isSubscribed, setIsSubscribed] = React.useState(subscribedToTrending);
  const [options, setOptions] = React.useState(tolSubscriptionOptions);
  const loadingRef = React.createRef();
  const subscribeLoadingRef = React.createRef();
  const unsubscribeLoadingRef = React.createRef();

  React.useEffect(() => {
    loadingRef.current && loadingRef.current.stop();
    subscribeLoadingRef.current && subscribeLoadingRef.current.stop();
    unsubscribeLoadingRef.current && unsubscribeLoadingRef.current.stop();
  });

  const subscribe = e => {
    e.preventDefault();

    subscribeLoadingRef.current.start();

    subscribeToTrending().then(() => {
      setIsSubscribed(true);
    });
  };

  const toggleTOLOption = option => {
    loadingRef.current.start();

    if (option === 'NEVER') {
      unsubscribeFromTrending('ALL').then(({ data }) => {
        setOptions([]);

        if (data.feedback_url) {
          window.location = data.feedback_url;
        }
      });
    } else if (options.includes(option)) {
      unsubscribeFromTrending(option).then(({ data }) => {
        const filteredOptions = options.filter(o => o !== option);
        setOptions(filteredOptions);

        if (data.feedback_url && filteredOptions.length === 0) {
          window.location = data.feedback_url;
        }
      });
    } else {
      subscribeToTrending(option).then(() => {
        setOptions([...options, option]);
      });
    }
  };

  const unsubscribe = e => {
    e.preventDefault();

    unsubscribeLoadingRef.current.start();

    unsubscribeFromTrending().then(({ data }) => {
      setIsSubscribed(false);

      if (data.feedback_url) {
        window.location = data.feedback_url;
      }
    });
  };

  return (
    <ThemeProvider theme={{ ...theme }}>
      <Container>
        <Title>Trending on Livewire Emails</Title>
        <Desc>
          Our flagship daily email, curating the best wires of the day and sent to your
          inbox. How frequently would you like to receive Trending on Livewire?
        </Desc>
        <SButtonGroup>
          {weeklyTOLEnabled && (
            <>
              <Button
                className={options.includes('DAILY') ? '' : 'inactive'}
                onClick={() => toggleTOLOption('DAILY')}
              >
                DAILY
              </Button>
              <Button
                className={options.includes('WEEKLY') ? '' : 'inactive'}
                onClick={() => toggleTOLOption('WEEKLY')}
              >
                WEEKLY
              </Button>
              <Button
                className={options.length === 0 ? '' : 'inactive'}
                disabled={options.length === 0}
                onClick={() => toggleTOLOption('NEVER')}
              >
                NEVER
              </Button>
              <Loading variant="inline" ref={loadingRef} />
            </>
          )}
          {!props.weeklyTOLEnabled && (
            <>
              <Loading ref={subscribeLoadingRef} variant="button">
                <Button
                  className={isSubscribed ? '' : 'inactive'}
                  disabled={isSubscribed}
                  onClick={subscribe}
                >
                  DAILY
                </Button>
              </Loading>
              <Loading ref={unsubscribeLoadingRef} variant="button">
                <Button
                  className={isSubscribed ? 'inactive' : ''}
                  disabled={!isSubscribed}
                  onClick={unsubscribe}
                >
                  NEVER
                </Button>
              </Loading>
            </>
          )}
        </SButtonGroup>
        {!weeklyTOLEnabled && isSubscribed && (
          <Hint>We’ll send you Trending on Livewire Daily at 6:30am</Hint>
        )}
        <Separator />
      </Container>
    </ThemeProvider>
  );
}

TrendingControl.propTypes = {
  subscribedToTrending: PropTypes.bool.isRequired,
  tolSubscriptionOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  weeklyTOLEnabled: PropTypes.bool.isRequired,
};
